import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions(entry.contentRect);
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);

  return dimensions;
};

const margin = { top: 30, right: 30, bottom: 30, left: 50 };
const bgColor = "#f9f9f9";
const color = [
  "#012749",
  "#8f7eae",
  "#b73274",
  "#ef4123",
  "#1192e8",
  "#00a2a5",
];

function Razrjesenja({ vladeJoin }) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const wrapDim = useResizeObserver(wrapperRef);
  let tooltipHidden = true;
  const tooltip = d3.select(".razrjesenja-tooltip");

  const maxDays = d3.max(
    vladeJoin,
    (d) => d3.timeDay.range(d.start, d.end).length
  );

  useEffect(() => {
    if (!wrapDim) return;

    const svg = d3.select(svgRef.current);

    svg.selectAll("*").remove();

    const width = wrapDim.width;
    const height = wrapDim.height < 400 ? 400 : wrapDim.height;

    svg.attr("width", "100%").attr("height", "100%");

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(vladeJoin, (d) => d.ostavke.length * 1.1)])
      .range([height - margin.bottom, margin.top]);

    const x = d3
      .scaleLinear()
      .domain([0, maxDays * 1.2])
      .range([margin.left, width - margin.right]);

    const line = d3
      .line()
      .x((d) => x(d.days))
      .y((d) => y(d.count))
      .curve(d3.curveStepAfter);

    const xAxis = (g) =>
      g
        .attr("transform", `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x).ticks(width / 100))
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .append("text")
            .attr("x", width - margin.right - 5)
            .attr("y", -10)
            .attr("fill", "#000")
            .attr("opacity", 0.7)
            .attr("font-size", 13)
            // .attr("font-weight", "bold")
            .attr("text-anchor", "end")
            .text("Broj dana na dužnosti premijera")
        );

    const yAxis = (g) =>
      g
        .attr("transform", `translate(${margin.left},0)`)
        .style("stroke-width", 1)
        .call(d3.axisLeft(y))
        .call((g) => g.select(".domain").remove())
        // .call((g) => g.select(".tick:last-of-type text").append("tspan").text(" $"))
        .call((g) =>
          g
            .append("text")
            .attr("x", 5)
            .attr("y", margin.top + 15)
            .attr("fill", "#000")
            .attr("opacity", 0.7)
            .attr("font-size", 13)
            // .attr("font-weight", "bold")
            .attr("text-anchor", "start")
            .text("Broj razrješenja")
        );

    const yGrid = (g) =>
      g
        .attr("transform", `translate(${margin.left},0)`)
        .call(d3.axisLeft(y).tickSize(-width + margin.left + margin.right))
        .call((g) => g.select(".domain").remove())
        .call((g) => g.selectAll("text").remove())
        .call((g) =>
          g.selectAll("line").attr("stroke", "lightgrey").attr("opacity", 0.5)
        );

    svg
      .append("rect")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", width - margin.left - margin.right)
      .attr("height", height - margin.bottom - margin.top)
      .attr("fill", bgColor);

    const axes = svg.append("g");
    axes.append("g").call(yGrid);
    axes.append("g").call(xAxis).attr("font-size", 13);
    axes.append("g").call(yAxis).attr("font-size", 13);
    // svg.append("g").call(yGrid);

    const lineGroup = svg.append("g").attr("class", "ostavke-lines");

    vladeJoin.forEach((premijer, index) => {
      let count = 0;
      const ostavke = premijer.ostavke;
      ostavke.forEach((ostavka) => {
        count++;
        ostavka.count = count;
        ostavka.days = d3.timeDay.range(premijer.start, ostavka.date).length;
      });

      lineGroup
        .append("path")
        .attr("stroke", bgColor)
        .attr("fill", "none")
        .attr("stroke-width", index === vladeJoin.length - 1 ? 10 : 5)
        .attr(
          "d",
          line([
            { count: 0, days: 0 },
            ...ostavke,
            {
              count: ostavke[ostavke.length - 1].count,
              days: d3.timeDay.range(premijer.start, premijer.end).length,
            },
          ])
        );

      lineGroup
        .append("path")
        .attr("stroke", color[index])
        .attr("opacity", index === vladeJoin.length - 1 ? 1 : 0.6)
        .attr("fill", "none")
        .attr("stroke-width", index === vladeJoin.length - 1 ? 5 : 3)
        .attr(
          "d",
          line([
            { count: 0, days: 0 },
            ...ostavke,
            {
              count: ostavke[ostavke.length - 1].count,
              days: d3.timeDay.range(premijer.start, premijer.end).length,
            },
          ])
        );

      lineGroup
        .append("circle")
        .attr("cx", x(d3.timeDay.range(premijer.start, premijer.end).length))
        .attr("cy", y(ostavke[ostavke.length - 1].count))
        // .attr("opacity", index === vladeJoin.length - 1 ? 1 : 0.6)
        .attr("r", 6)
        .attr("stroke", bgColor)
        .attr("stroke-width", 3)
        .attr("fill", color[index]);

      lineGroup
        .append("text")
        .attr("x", x(d3.timeDay.range(premijer.start, premijer.end).length))
        .attr("y", y(ostavke[ostavke.length - 1].count))
        .attr("dx", 10)
        .attr("dy", -10)
        .attr("text-anchor", "middle")
        .attr("font-weight", 700)
        .attr("font-family", "Roboto")
        .style("stroke", bgColor)
        .style("stroke-width", 2.5)
        .text(`${premijer.premijer}`);

      lineGroup
        .append("text")
        .attr("x", x(d3.timeDay.range(premijer.start, premijer.end).length))
        .attr("y", y(ostavke[ostavke.length - 1].count))
        .attr("dx", 10)
        .attr("dy", -10)
        .attr("text-anchor", "middle")
        .attr("opacity", index === vladeJoin.length - 1 ? 1 : 0.8)
        .attr("font-weight", 700)
        .attr("font-family", "Roboto")
        .attr("fill", color[index])
        .text(`${premijer.premijer}`)
        .on("mouseover", function (event, d) {
          tooltipHidden = !tooltipHidden;
          // let top = event.y;
          // if (top > height * 0.65) top -= 200;

          // let left = event.x;
          // if (left > width * 0.65) left -= 400;

          tooltip
            // .style("top", `${top}px`)
            // .style("left", `${left}px`)
            .style("visibility", tooltipHidden ? "hidden" : "visible");

          tooltip.select("#tooltip-premijer").text(premijer.premijer);
          tooltip
            .select("#tooltip-oddo")
            .text(
              `${premijer.start.toLocaleDateString()} - ${premijer.end.toLocaleDateString()}`
            );

          const lista = tooltip.select(".ostavke-lista");
          lista.selectAll("*").remove();
          premijer.ostavke.forEach((ostavka) => {
            lista
              .append("li")
              .text(`${ostavka.name} - ${ostavka.date.toLocaleDateString()}`);
          });
        })
        .on("mousemove", function (event, d) {
          let tooltipWidth = +tooltip.node().getBoundingClientRect().width;
          let tooltipHeight = +tooltip.node().getBoundingClientRect().height;
          // console.log();

          let top = event.y + 20;
          if (top > height * 0.50) top -= tooltipHeight + 50;

          let left = event.x + 50;
          if (event.x > width * 0.4) left -= tooltipWidth + 50;

          // console.log(event.x, width * 0.4, event.x > width * 0.4);
          // console.log(top, tooltipHeight)
          console.log(event.x, left)

          tooltip.style("top", `${top}px`).style("left", `${left}px`);
        })
        .on("mouseout", function (event, d) {
          d3.select(this).classed("highlighted", false);
          tooltipHidden = true;
          tooltip.style("visibility", tooltipHidden ? "hidden" : "visible");
        });
    });

    lineGroup.selectAll("text").raise();
  }, [wrapDim]);

  return (
    <div ref={wrapperRef} className="viz-wrapper">
      <svg ref={svgRef}></svg>
      <div className="razrjesenja-tooltip">
        <div className="tooltip-title-row">
          <h4 id="tooltip-premijer">Premijer</h4>
        </div>
        <div className="tooltip-title-row">
          <h6 id="tooltip-oddo">Vladavina od do</h6>
        </div>
        <span className="ostavke-label">Razrješenja:</span>
        <ol className="ostavke-lista"></ol>
        <div className="razrjesenja-tooltip-content"></div>
      </div>
    </div>
  );
}

export default Razrjesenja;
